import type {
  MrtColumnFilter,
  MrtCountTarget,
} from '@app/src/pages/ResourceCollection/MrtCollection/mrtCollectionTypes'
import type { Unit } from '@app/src/types/resourceExplorer'
import React from 'react'
import type { IntlShape } from 'react-intl'
import SpendFilterEditor from './SpendFilterEditor'
import { spendChipSubject, spendDisplayPeriod, summariseSpendFilter } from './spendFilterValue'
import type { SpendFilterOptions } from './useSpendFilterOptions'

type SpendColumnFilterArgs = {
  // What the picker de-duplicates on. Where a table has a spend column of its own this is that column's
  // own field; a filter-only entry names the field it filters.
  field: string
  options: SpendFilterOptions
  formatMessage: IntlShape['formatMessage']
  // Built from the collection's own spend path, and its own scope where it has one, so only this part
  // differs between tables.
  toFilterGroups: MrtColumnFilter['toFilterGroups']
  currencyUnit?: Unit
  // Where the spend groups are counted under the table's other filters. Without it they are counted on all the
  // providers, which a table whose rows aren't the providers has no filters for.
  countTarget?: MrtCountTarget
}

/**
 * The one spend filter, wired the same way on every table that has spend: the same editor, the same chip
 * and the same operators, over whichever `spends` collection the table reaches.
 */
export const spendColumnFilter = ({
  field,
  options,
  formatMessage,
  toFilterGroups,
  currencyUnit,
  countTarget,
}: SpendColumnFilterArgs): MrtColumnFilter => ({
  field,
  variant: 'custom',
  renderEditor: ({ value, onChange, header, otherFilterGroups }) => (
    <SpendFilterEditor
      options={options}
      currencyUnit={currencyUnit}
      value={value}
      onChange={onChange}
      header={header}
      countFilterGroups={countTarget ? otherFilterGroups : []}
      countTarget={countTarget}
    />
  ),
  isHeaderRenderedByEditor: true,
  renderSummary: value =>
    summariseSpendFilter(value, {
      noSpendLabel: formatMessage({ id: 'dashboard.sourcing.overallRisk.noSpend' }),
      orWord: formatMessage({ id: 'filters.multiStep.or' }),
      currencyUnit,
    }),
  getChipSubject: (value, columnLabel) =>
    spendChipSubject(value, columnLabel, {
      anyPeriodLabel: formatMessage({ id: 'schemas.filter.spendAnyPeriod' }),
      // The same default the query applies, so a restored legacy range names the period it filters on.
      defaultPeriodName: options.defaultPeriodName,
    }),
  // Only "is": the presence question the operators used to ask is now part of the selection itself, as
  // "not added" over any period. toFilterGroups still answers them for filters persisted before that.
  toFilterGroups,
})

/** The Spend column's header, carrying the year its cells show: the filter's, else the default, "Spend 2025". */
export const spendHeader = (
  filterValue: unknown,
  defaultPeriodName: string | undefined,
  formatMessage: IntlShape['formatMessage'],
): { header?: string } => {
  const periodName = spendDisplayPeriod(filterValue, defaultPeriodName)
  return periodName ? { header: formatMessage({ id: 'export.schemas.provider.spend' }, { year: periodName }) } : {}
}
